import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  ButtonBase,
  useTheme,
} from "@mui/material";
import {
  HomeOutlined,
  Home,
  WhatsApp,
  BusinessOutlined,
  PersonOutline,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { REACT_APP_NAV_ROUTES } from "../../../router/AppNavigation";
import { useAuth } from "../../../contexts/AuthProvider";
import { useBillingCompanyContext } from "../../../providers";

export interface MobileBottomNavigationProps {
  onOpenCompanySheet: () => void;
}

export const MobileBottomNavigation: React.FC<MobileBottomNavigationProps> = ({
  onOpenCompanySheet,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { comapny } = useBillingCompanyContext();

  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);

  // Directional scroll hide & reveal behavior (Instagram / Threads style)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY =
        window.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0;
      const deltaY = currentScrollY - lastScrollY.current;

      // Always show when user is near the very top of the page
      if (currentScrollY <= 25) {
        setIsHidden(false);
      }
      // Hide when user scrolls down intentionally
      else if (deltaY > 8 && currentScrollY > 60) {
        setIsHidden(true);
      }
      // Immediately reveal when user scrolls up by even a small amount
      else if (deltaY < -6) {
        setIsHidden(false);
      }

      // Always reveal when user reaches near the bottom of the page so they can navigate
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      if (currentScrollY + windowHeight >= docHeight - 35) {
        setIsHidden(false);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Always show the navigation bar when route changes
  useEffect(() => {
    setIsHidden(false);
  }, [location.pathname]);

  const isDashboard =
    location.pathname === "/" ||
    location.pathname === "/dashboard" ||
    location.pathname === REACT_APP_NAV_ROUTES.dashboard ||
    location.pathname.startsWith("/dashboard");

  const isChat = location.pathname.startsWith(REACT_APP_NAV_ROUTES.chat);
  const isProfile =
    location.pathname.startsWith("/my-profile") ||
    location.pathname.startsWith("/users/manage");

  const navItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: isDashboard ? (
        <Home sx={{ fontSize: "1.75rem" }} />
      ) : (
        <HomeOutlined sx={{ fontSize: "1.75rem" }} />
      ),
      active: isDashboard,
      onClick: () => {
        if (!isDashboard) {
          navigate(REACT_APP_NAV_ROUTES.dashboard);
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      },
    },
    {
      id: "chats",
      label: "Chats",
      icon: (
        <WhatsApp
          sx={{
            fontSize: "1.65rem",
            color: isChat
              ? isDark
                ? "#4ade80"
                : "#16a34a"
              : "inherit",
          }}
        />
      ),
      active: isChat,
      onClick: () => {
        navigate(REACT_APP_NAV_ROUTES.chat);
      },
    },
    {
      id: "company",
      label: comapny?.company_outlet_name || "Company",
      icon: <BusinessOutlined sx={{ fontSize: "1.65rem" }} />,
      active: false,
      onClick: onOpenCompanySheet,
    },
    {
      id: "profile",
      label: "Profile",
      icon: user?.name ? (
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <Avatar
            sx={{
              width: 30,
              height: 30,
              fontSize: "0.85rem",
              fontWeight: 700,
              bgcolor: isProfile
                ? theme.palette.primary.main
                : isDark
                ? "rgba(255,255,255,0.18)"
                : "rgba(15, 23, 42, 0.08)",
              color: isProfile
                ? "#fff"
                : isDark
                ? "#fff"
                : "#0f172a",
              border: isProfile
                ? `2px solid ${isDark ? "#ffffff" : theme.palette.primary.main}`
                : `1.5px solid ${isDark ? "rgba(255,255,255,0.2)" : "rgba(15, 23, 42, 0.15)"}`,
              transition: "all 0.2s ease",
            }}
          >
            {user.name.charAt(0).toUpperCase()}
          </Avatar>
          {/* Subtle status dot matching Instagram / Threads avatar style */}
          <Box
            sx={{
              position: "absolute",
              bottom: -1,
              right: -1,
              width: 9,
              height: 9,
              borderRadius: "50%",
              backgroundColor: isProfile ? "#ef4444" : "#22c55e",
              border: `2px solid ${isDark ? "#0f172a" : "#ffffff"}`,
            }}
          />
        </Box>
      ) : (
        <PersonOutline sx={{ fontSize: "1.7rem" }} />
      ),
      active: isProfile,
      onClick: () => {
        if (user?.userUuid) {
          navigate(`/users/manage/${user.userUuid}`);
        } else {
          navigate(REACT_APP_NAV_ROUTES.MyProfile || "/my-profile");
        }
      },
    },
  ];

  return (
    <Box
      component="nav"
      aria-label="Mobile Bottom Navigation"
      sx={{
        position: "fixed",
        bottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
        left: "50%",
        transform: isHidden
          ? "translate(-50%, calc(100% + 40px))"
          : "translate(-50%, 0)",
        opacity: isHidden ? 0 : 1,
        pointerEvents: isHidden ? "none" : "auto",
        transition:
          "transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.25s ease, box-shadow 0.25s ease",
        zIndex: 1100,
        width: "min(380px, calc(100% - 32px))",
        height: 56,
        borderRadius: "9999px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-around",
        px: 1,
        backgroundColor: isDark
          ? "rgba(15, 23, 42, 0.85)"
          : "rgba(255, 255, 255, 0.82)",
        backdropFilter: "blur(24px) saturate(180%)",
        WebkitBackdropFilter: "blur(24px) saturate(180%)",
        border: isDark
          ? "1px solid rgba(255, 255, 255, 0.12)"
          : "1px solid rgba(255, 255, 255, 0.75)",
        boxShadow: isDark
          ? "0 16px 40px -6px rgba(0, 0, 0, 0.6), 0 4px 16px -2px rgba(0, 0, 0, 0.4), inset 0 1px 1px 0 rgba(255, 255, 255, 0.15)"
          : "0 12px 32px -4px rgba(0, 0, 0, 0.14), 0 4px 12px -2px rgba(0, 0, 0, 0.06), inset 0 1px 1.5px 0 rgba(255, 255, 255, 0.95)",
      }}
    >
      {navItems.map((item) => {
        const activeColor = isDark ? "#ffffff" : "#0f172a";
        const inactiveColor = isDark
          ? "rgba(255, 255, 255, 0.72)"
          : "#334155";
        const color = item.active ? activeColor : inactiveColor;

        return (
          <ButtonBase
            key={item.id}
            onClick={item.onClick}
            aria-label={item.label}
            sx={{
              position: "relative",
              flex: 1,
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "9999px",
              color,
              transition: "transform 0.15s ease",
              "&:active": {
                transform: "scale(0.9)",
              },
            }}
          >
            {/* Active Capsule Highlight (matching Instagram / Threads style pill indicator) */}
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 54,
                height: 40,
                borderRadius: "20px",
                backgroundColor: item.active
                  ? isDark
                    ? "rgba(255, 255, 255, 0.14)"
                    : "rgba(15, 23, 42, 0.08)"
                  : "transparent",
                color,
                transition:
                  "background-color 0.2s ease, transform 0.2s ease",
              }}
            >
              {item.icon}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
};
